import { toast } from "~served/ui-comps/toast";
import { recalculateCurrentCart, resetCurrentCart, getState } from "~/store";
import {
	GetCurrentLocationQuery,
	GetCurrentVenueQuery,
	LocationSubscriptionSummaryFragment,
	SubscribeToDeviceOrdersSubscription,
	VenueSubscriptionSummaryFragment,
} from "~/types/__generated/gql/graphql";

type VenueResult = GqlResultOf<GetCurrentVenueQuery> | undefined;
type LocationResult = GqlResultOf<GetCurrentLocationQuery> | undefined;
type VenuePricingResult = VenueResult | VenueSubscriptionSummaryFragment;
type LocationPricingResult =
	| LocationResult
	| LocationSubscriptionSummaryFragment;
type DeviceOrderEvent =
	SubscribeToDeviceOrdersSubscription["subscribeToDeviceOrders"];
type DeviceOrderPayload = NonNullable<DeviceOrderEvent["payload"]>;
interface OrderToastPayload {
	_id: string;
	index?: number | null;
}

const toastOrderMessage = (
	order: OrderToastPayload,
	message: string,
	type: "success" | "info" | "warning" | "error" = "info",
) => {
	toast[type](message.replace("%index%", String(order.index)), {
		id: `consumer-order:${order._id}:${message}`,
	});
};

const getVenuePricingSignature = (venue?: VenuePricingResult) =>
	venue
		? JSON.stringify({
				vat: venue.vat,
				service_charge: venue.service_charge,
				service_charge_bd: venue.service_charge_bd,
				default_service_charge_type: venue.default_service_charge_type,
				tax: venue.tax,
			})
		: null;

const getLocationPricingSignature = (location?: LocationPricingResult) =>
	location
		? JSON.stringify({
				no_vat: location.no_vat,
				no_service_charge: location.no_service_charge,
			})
		: null;

export const hasPricingContextChanged = ({
	prevVenue,
	nextVenue,
	prevLocation,
	nextLocation,
}: {
	prevVenue?: VenuePricingResult;
	nextVenue?: VenuePricingResult;
	prevLocation?: LocationPricingResult;
	nextLocation?: LocationPricingResult;
}) =>
	getVenuePricingSignature(prevVenue) !== getVenuePricingSignature(nextVenue) ||
	getLocationPricingSignature(prevLocation) !==
		getLocationPricingSignature(nextLocation);

export const reconcileCartForRealtimeContext = ({
	prevVenue,
	nextVenue,
	prevLocation,
	nextLocation,
}: {
	prevVenue?: VenuePricingResult;
	nextVenue?: VenuePricingResult;
	prevLocation?: LocationPricingResult;
	nextLocation?: LocationPricingResult;
}) => {
	if (!getState().cart.items.length) return;
	if (
		hasPricingContextChanged({
			prevVenue,
			nextVenue,
			prevLocation,
			nextLocation,
		})
	) {
		recalculateCurrentCart();
		toast.info("Cart prices were updated.", {
			id: "consumer-cart-repriced",
		});
	}
};

export const reconcileCartForMenuEvent = ({
	entityType,
	entityId,
}: {
	entityType: "menu" | "category" | "item";
	entityId?: string;
}) => {
	if (!entityId) return;

	const cartItems = getState().cart.items;
	if (!cartItems.length) return;

	const shouldReset =
		(entityType === "item" &&
			cartItems.some((item) => item.item?.toString() === entityId)) ||
		(entityType === "category" &&
			cartItems.some((item) => item.category?.toString() === entityId));

	if (!shouldReset) return;

	resetCurrentCart();
	toast.warning("Menu changed. Your cart was cleared.", {
		id: "consumer-cart-reset-menu-change",
	});
};

export const notifyForDeviceOrderEvent = (event: DeviceOrderEvent) => {
	const order = event.payload;
	if (!order) return;

	switch (event.type) {
		case "PLACED":
		case "PAID":
			return;

		case "PAYMENT_ACCEPTED":
			toastOrderMessage(
				order,
				"Payment accepted for order #%index%.",
				"success",
			);
			return;

		case "PREP_TIME_CONFIRMATION_REQUIRED":
			notifyForPrepTimeEvent(order, "needs confirmation");
			return;

		case "PREP_TIME_UPDATED":
			notifyForPrepTimeEvent(order, "updated");
			return;

		case "MOVED_TO_PREPARING":
			toastOrderMessage(
				order,
				"Order #%index% is confirmed and being prepared.",
				"success",
			);
			return;

		case "PROCESSED":
			toastOrderMessage(order, "Order #%index% is ready.", "success");
			return;

		case "CANCELLED":
			toastOrderMessage(order, "Order #%index% was cancelled.", "error");
			return;

		default:
			assertNeverDeviceOrderEvent(event.type);
	}
};

const notifyForPrepTimeEvent = (
	order: DeviceOrderPayload,
	mode: "needs confirmation" | "updated",
) => {
	if (!order.prep_time) return;

	const message =
		mode === "needs confirmation"
			? `Order #${order.index} needs prep time confirmation for ${order.prep_time} min.`
			: `Order #${order.index} will be ready in about ${order.prep_time} min.`;

	toast.info(message, {
		id: `consumer-order:${order._id}:prep-time:${mode}:${order.prep_time}`,
	});
};

const assertNeverDeviceOrderEvent = (type: never) => {
	console.warn("Unhandled device order event", type);
};
